import { createContext, forwardRef, useContext, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { Button, buttonBase, buttonSizes, buttonVariants, type ButtonSize, type ButtonVariant } from '../../primitives/Button/Button';
import { Tooltip } from '../../primitives/Tooltip/Tooltip';
import { defaultRenderLink, useEntityLinkContext, type RenderLink } from '../EntityLink/EntityLinkContext';
import { resolveNextAction, type NextAction, type NextActionRoutes, type NextActionTone } from './registry';
import './baseline';
import { useDsT } from '../../services/i18n';

export type NextActionPermissionCheck = (permission: string) => boolean;

const PermissionsContext = createContext<NextActionPermissionCheck | null>(null);

/** Supplies the permission check every `NextActionButton` below uses when no `can` prop is given. */
export function NextActionPermissionsProvider({ can, children }: { can: NextActionPermissionCheck; children: ReactNode }) {
  return <PermissionsContext.Provider value={can}>{children}</PermissionsContext.Provider>;
}

const ExtraContext = createContext<Record<string, unknown> | null>(null);

/**
 * Supplies context every `NextActionButton` below passes to the registry's builders under `extra` (the app's `t`,
 * so a next action reads in the viewer's language wherever it is drawn — card, record, or a grid cell).
 * A button's own `extra` wins key by key.
 */
/** The `extra` a `NextActionExtraProvider` above supplies (the viewer's translator, the row) — for hosts that resolve the action themselves. */
export function useNextActionExtra(): Record<string, unknown> | null {
  return useContext(ExtraContext);
}

export function NextActionExtraProvider({ extra, children }: { extra: Record<string, unknown>; children: ReactNode }) {
  return <ExtraContext.Provider value={extra}>{children}</ExtraContext.Provider>;
}

export type NextActionDenied = 'disable' | 'hide';

export interface NextActionButtonProps {
  entityType: string;
  status: string;
  id: string;
  /** Route builders handed to the registry builder (`{ track: (id) => … }`). */
  routes?: NextActionRoutes;
  /** Anything the builder may need beyond id, status and routes. */
  extra?: Record<string, unknown>;
  /** Permission check; falls back to `NextActionPermissionsProvider`, then to "allowed". */
  can?: NextActionPermissionCheck;
  /** What to do when the permission is missing (default `disable`). */
  denied?: NextActionDenied;
  /** Called for actions without a `href` (and for those with one, before navigation). */
  onAction?: (action: NextAction, ctx: { entityType: string; id: string; status: string }) => void;
  size?: ButtonSize;
  /** Base variant for actions without a tone (default `soft`). */
  variant?: ButtonVariant;
  renderLink?: RenderLink;
  /** Rendered when the status has no next action. */
  fallback?: ReactNode;
  /** Tooltip / accessible hint on a denied action. Default `You do not have permission`. */
  deniedLabel?: string;
  /** Replaces the visible text (the action's label stays the accessible name) — the next-step hero's "Start". */
  displayLabel?: ReactNode;
  /** Wins over the action's tone (the hero's light button on the accent band). */
  forceVariant?: ButtonVariant;
  className?: string;
  testId?: string;
}

const toneVariant: Record<NextActionTone, ButtonVariant | undefined> = { default: undefined, primary: 'primary', danger: 'danger' };

/**
 * "What do I do with this row?" answered by the registry: one button, one verb, with the app's route or handler,
 * and disabled (or hidden) when the viewer lacks the permission.
 */
export const NextActionButton = forwardRef<HTMLElement, NextActionButtonProps>(function NextActionButton(
  { entityType, status, id, routes, extra, can, denied = 'disable', onAction, size = 'sm', variant = 'soft', renderLink, fallback = null, deniedLabel: deniedLabelProp, displayLabel, forceVariant, className, testId },
  ref,
) {
  const ctxCan = useContext(PermissionsContext);
  const ctxExtra = useContext(ExtraContext);
  const linkCtx = useEntityLinkContext();
  const t = useDsT();
  const deniedLabel = deniedLabelProp ?? t('ds.nextActionButton.denied', 'You do not have permission');
  const action = resolveNextAction(entityType, status, { id, routes, extra: ctxExtra ? { ...ctxExtra, ...extra } : extra });
  if (!action) return <>{fallback}</>;
  const check = can ?? ctxCan;
  const allowed = !action.permission || !check || check(action.permission);
  if (!allowed && denied === 'hide') return <>{fallback}</>;
  const label = action.labelKey ? t(action.labelKey, action.label) : action.label;
  const resolvedVariant = forceVariant ?? toneVariant[action.tone ?? 'default'] ?? variant;
  const icon = action.icon ? <Icon name={action.icon} size={14} aria-hidden="true" /> : undefined;
  const fire = () => {
    onAction?.(action, { entityType, id, status });
    action.onClick?.();
  };
  const data = { 'data-entity-type': entityType, 'data-status': status, 'data-action': action.label, 'data-testid': testId } as const;

  if (action.href && allowed) {
    const link = renderLink ?? linkCtx.renderLink ?? defaultRenderLink;
    const content = (
      <>
        {icon ? <span className="zo-button-icon inline-flex shrink-0 items-center">{icon}</span> : null}
        <span className="zo-button-label truncate">{displayLabel ?? label}</span>
      </>
    );
    return (
      <>
        {link({
          href: action.href,
          className: cn(buttonBase, buttonVariants[resolvedVariant], buttonSizes[size], className),
          children: content,
          ...(displayLabel != null ? { 'aria-label': label } : {}),
          onClick: onAction || action.onClick ? fire : undefined,
          ...data,
        })}
      </>
    );
  }

  const button = (
    <Button
      ref={ref as never}
      size={size}
      variant={resolvedVariant}
      iconStart={icon}
      disabled={!allowed}
      aria-disabled={!allowed || undefined}
      aria-label={!allowed ? `${label} — ${deniedLabel}` : displayLabel != null ? label : undefined}
      onClick={allowed ? fire : undefined}
      className={className}
      {...data}
    >
      {displayLabel ?? label}
    </Button>
  );
  return allowed ? button : <Tooltip content={deniedLabel}><span className="inline-flex">{button}</span></Tooltip>;
});
